'use client';

import { type MouseEvent, type ReactNode } from 'react';
import { Icon } from '@/components/ui/icon';
import { ThumbImage } from '@/components/ui/thumb-image';
import { VariantChip } from '@/components/ui/variant-chip';
import styles from './meal-plan-glance.module.css';

const cx = (...names: Array<string | false | null | undefined>) =>
  names.filter(Boolean).join(' ');

/** A single planned dish — shape mirrors {@link GlanceDish} from the live read. */
export interface MealPlanGlanceDish {
  recipeId: string;
  slug: string;
  title: string;
  variantType: 'quick' | 'classic' | 'bougie';
  /** YYYY-MM-DD. */
  scheduledDate: string;
  imageUrl: string | null;
  /** prep + cook for the planned variant; null when the variant has no times. */
  totalMinutes: number | null;
  /** Prep / active(cook) split — drives the chip's hover title. */
  prepMinutes?: number | null;
  cookMinutes?: number | null;
}

/** Shopping summary — `is_checked` semantics only (no pantry model). */
export interface MealPlanGlanceShopping {
  /** Unchecked items — still to buy. */
  toBuy: number;
  /** Checked items — already in the basket. */
  inBasket: number;
}

export interface MealPlanGlanceProps {
  /** Upcoming planned dishes, already sliced to the teaser window. */
  upcoming: MealPlanGlanceDish[];
  shopping: MealPlanGlanceShopping;
  /** Optional short list of shopping-item names (the live read can supply these later). */
  items?: string[];
  /** Today's date (YYYY-MM-DD) — marks the current day and drives the "Today" label. */
  today: string;
  /** BCP-47 locale used only to format weekday/day labels. Defaults to `'en'`. */
  locale?: string;

  planHref?: string;
  listHref?: string;
  onOpenPlan?: () => void;
  onOpenList?: () => void;
  /** When provided, each dish row becomes a link to this target. */
  getDishHref?: (dish: MealPlanGlanceDish) => string;
  /**
   * Serializable alternative to `getDishHref` (safe to pass from a Server
   * Component): the dish row links to `dishHrefBase + dish.slug`.
   */
  dishHrefBase?: string;
  onOpenDish?: (dish: MealPlanGlanceDish) => void;

  className?: string;

  // ── labels (every user-visible string; the page wires i18n) ──
  /** Accessible name for the whole panel, e.g. "At a glance". */
  regionLabel: string;
  /** "This week" eyebrow. */
  weekLabel: string;
  /** "Meal plan" link. */
  planLinkLabel: string;
  /** Current-day label, e.g. "Today". */
  todayLabel: string;
  /** "Shopping list" eyebrow. */
  shoppingLabel: string;
  /** "Open" link. */
  listLinkLabel: string;
  /** "to buy". */
  toBuyLabel: string;
  /** "in basket". */
  inBasketLabel: string;
  /** Minute unit, e.g. "min". Defaults to `"min"`. */
  minutesUnitLabel?: string;
  /** Template for the time chip's hover split, with `{prep}`/`{cook}`
   *  placeholders, e.g. `"{prep} min prep · {cook} min active"`. */
  minutesSplitLabel?: string;
  /** Template for the "+N more" items row, e.g. `"+{count} more"`. */
  moreItemsLabel?: string;
  emptyPlanTitle: string;
  emptyPlanText: string;
  emptyShoppingTitle: string;
  emptyShoppingText: string;
}

/**
 * MealPlanGlance — the signed-in "at a glance" panel: two calm columns on a
 * surface card. Left: the next few days' planned dishes. Right: a short
 * shopping-list summary (to-buy vs. in-basket, a progress bar, and a few
 * item names). Presentational only — data in via props, i18n wired by the page.
 */
export function MealPlanGlance({
  upcoming,
  shopping,
  items = [],
  today,
  locale = 'en',
  planHref = '#',
  listHref = '#',
  onOpenPlan,
  onOpenList,
  getDishHref,
  dishHrefBase,
  onOpenDish,
  className,
  regionLabel,
  weekLabel,
  planLinkLabel,
  todayLabel,
  shoppingLabel,
  listLinkLabel,
  toBuyLabel,
  inBasketLabel,
  minutesUnitLabel = 'min',
  minutesSplitLabel,
  moreItemsLabel,
  emptyPlanTitle,
  emptyPlanText,
  emptyShoppingTitle,
  emptyShoppingText,
}: MealPlanGlanceProps) {
  const { toBuy, inBasket } = shopping;
  const total = toBuy + inBasket;
  const pct = total > 0 ? Math.round((inBasket / total) * 100) : 0;
  const hasShopping = items.length > 0 || toBuy > 0 || inBasket > 0;
  // Fully empty (no plan AND no shopping) → the two tall empty blocks add no value
  // and eat the top of a phone screen. On mobile we collapse to a single compact
  // nudge (CSS); desktop keeps the full panel + its gentle onboarding cue.
  const isEmpty = upcoming.length === 0 && !hasShopping;

  const formatDay = (scheduledDate: string): string => {
    if (scheduledDate === today) return todayLabel;
    // Parse at noon to avoid any timezone/DST day-shift when formatting.
    const date = new Date(`${scheduledDate}T12:00:00`);
    if (Number.isNaN(date.getTime())) return scheduledDate;
    return new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric' }).format(date);
  };

  const linkClick =
    (cb?: () => void) =>
    (e: MouseEvent<HTMLAnchorElement>) => {
      if (cb) {
        e.preventDefault();
        cb();
      }
    };

  return (
    <section className={cx(styles.glance, isEmpty && styles.isEmpty, className)} aria-label={regionLabel}>
      {/* Mobile-only compact nudge when the whole panel is empty (CSS-gated) — the
          full two-column empty state below still shows on desktop. */}
      {isEmpty ? (
        <a href={planHref} className={styles.emptyCompact} onClick={linkClick(onOpenPlan)}>
          <Icon name="calendar" size={16} />
          <span className={styles.emptyCompactText}>{emptyPlanTitle}</span>
          <Icon name="arrow-right" size={15} className={styles.emptyCompactGo} />
        </a>
      ) : null}

      {/* ── This week ── */}
      <div className={styles.col}>
        <div className={styles.head}>
          <span className={styles.eyebrow}>
            <Icon name="calendar" size={14} />
            {weekLabel}
          </span>
          <a href={planHref} className={styles.link} onClick={linkClick(onOpenPlan)}>
            {planLinkLabel}
            <Icon name="arrow-right" size={14} />
          </a>
        </div>

        {upcoming.length > 0 ? (
          <ul className={styles.days}>
            {upcoming.map((dish) => {
              const isToday = dish.scheduledDate === today;
              const href =
                getDishHref?.(dish) ??
                (dishHrefBase ? `${dishHrefBase}${dish.slug}` : undefined);
              const interactive = Boolean(href || onOpenDish);
              // Visible cook-time (the chip label); hover shows the prep/cook
              // split when both are known (interpolated from minutesSplitLabel,
              // mirroring the moreItemsLabel template pattern).
              const timeLabel =
                dish.totalMinutes != null
                  ? `${dish.totalMinutes} ${minutesUnitLabel}`
                  : null;
              const splitTitle =
                minutesSplitLabel != null &&
                dish.prepMinutes != null &&
                dish.cookMinutes != null
                  ? minutesSplitLabel
                      .replace('{prep}', String(dish.prepMinutes))
                      .replace('{cook}', String(dish.cookMinutes))
                  : timeLabel;
              const inner: ReactNode = (
                <>
                  <span className={styles.thumb}>
                    {dish.imageUrl ? (
                      <ThumbImage
                        thumbSrc={dish.imageUrl}
                        fallbackSrc={dish.imageUrl}
                        alt={dish.title}
                        width={40}
                        height={40}
                        className={styles.thumbImg}
                      />
                    ) : (
                      <span className={styles.thumbFallback} aria-hidden="true">
                        <Icon name="fork-knife" size={18} />
                      </span>
                    )}
                  </span>
                  <span className={styles.dishwrap}>
                    <span className={styles.daylabel}>{formatDay(dish.scheduledDate)}</span>
                    <span className={styles.dishname}>{dish.title}</span>
                  </span>
                  {/* Cook-time as a variant-tinted chip: the tier reads from the
                      tint + icon, the visible label is the time, and hover shows
                      the prep/cook split. */}
                  {timeLabel != null ? (
                    dish.variantType ? (
                      <VariantChip
                        variant={dish.variantType}
                        label={timeLabel}
                        title={splitTitle ?? undefined}
                        className={styles.vchip}
                      />
                    ) : (
                      <span className={styles.dishtime}>
                        <Icon name="timer" size={12} />
                        {timeLabel}
                      </span>
                    )
                  ) : null}
                  {interactive ? (
                    <Icon name="chevron-right" size={15} className={styles.go} />
                  ) : null}
                </>
              );
              const rowClass = cx(styles.day, isToday && styles.isToday, interactive && styles.dayLink);
              return (
                <li key={`${dish.recipeId}:${dish.scheduledDate}`}>
                  {interactive ? (
                    <a
                      className={rowClass}
                      href={href ?? '#'}
                      onClick={onOpenDish ? (e) => {
                        e.preventDefault();
                        onOpenDish(dish);
                      } : undefined}
                    >
                      {inner}
                    </a>
                  ) : (
                    <span className={rowClass}>{inner}</span>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          <div className={styles.blank}>
            <span className={styles.blankicon}>
              <Icon name="calendar" size={20} />
            </span>
            <p className={styles.blanktitle}>{emptyPlanTitle}</p>
            <p className={styles.blanktext}>{emptyPlanText}</p>
          </div>
        )}
      </div>

      {/* ── Shopping list ── */}
      <div className={cx(styles.col, styles.colShop)}>
        <div className={styles.head}>
          <span className={styles.eyebrow}>
            <Icon name="cart" size={14} />
            {shoppingLabel}
          </span>
          <a href={listHref} className={styles.link} onClick={linkClick(onOpenList)}>
            {listLinkLabel}
            <Icon name="arrow-right" size={14} />
          </a>
        </div>

        {hasShopping ? (
          <>
            <div className={styles.shopstats}>
              <span className={styles.stat}>
                <b>{toBuy}</b> {toBuyLabel}
              </span>
              <span className={styles.statdot} aria-hidden="true">
                ·
              </span>
              <span className={cx(styles.stat, styles.statMuted)}>
                <b>{inBasket}</b> {inBasketLabel}
              </span>
            </div>
            <div className={styles.bar} role="img" aria-label={`${pct}% ${inBasketLabel}`}>
              <span style={{ width: `${pct}%` }} />
            </div>
            {items.length > 0 ? (
              <ul className={styles.list}>
                {items.slice(0, 4).map((name, i) => (
                  <li key={`${name}:${i}`} className={styles.item}>
                    <span className={styles.itemdot} aria-hidden="true" />
                    <span className={styles.itemname}>{name}</span>
                  </li>
                ))}
                {items.length > 4 ? (
                  <li className={cx(styles.item, styles.itemMore)}>
                    <a href={listHref} className={styles.moreitem} onClick={linkClick(onOpenList)}>
                      {(moreItemsLabel ?? '+{count}').replace('{count}', String(items.length - 4))}
                    </a>
                  </li>
                ) : null}
              </ul>
            ) : null}
          </>
        ) : (
          <div className={styles.blank}>
            <span className={styles.blankicon}>
              <Icon name="cart" size={20} />
            </span>
            <p className={styles.blanktitle}>{emptyShoppingTitle}</p>
            <p className={styles.blanktext}>{emptyShoppingText}</p>
          </div>
        )}
      </div>
    </section>
  );
}
